import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import { yieldToMainBackground } from 'utils/schedulerUtils';
import s from './InViewObserver.scss';

const InViewObserver = props => {
  const {
    inViewCallback,
    ga,
    children,
    viewMargin,
    unSupportedCallback,
    doNotUnobserve,
    className,
    wrapperClass,
    placeHolderClass,
    thresholdMargin,
    id,
  } = props;
  const elRef = useRef();
  let inViewObserver;

  const handleIntersection = async entries => {
    const entryToCheck = entries[0];
    if (
      entryToCheck &&
      (entryToCheck.isIntersecting || entryToCheck.intersectionRatio > 0)
    ) {
      await yieldToMainBackground();
      inViewCallback();
      // **** Important Logic ***
      // ga call and inViewCallback are both called based on the rootMargin provided
      // if rootMargin is 100px for top, then ga and inViewCallback would be executed 100px before coming into view
      if (ga && ga.category) {
        analyticsWrapper(
          'ga',
          'send',
          'event',
          ga.category,
          ga.action,
          ga.label,
          ga.value,
        );
      }

      // stop watching this element
      if (!doNotUnobserve) {
        inViewObserver.unobserve(entryToCheck.target);
      }
    }
  };

  useEffect(() => {
    if (elRef && elRef.current && 'IntersectionObserver' in window) {
      inViewObserver = new IntersectionObserver(
        handleIntersection,
        // Root Margin given to load images just before coming to viewport
        {
          rootMargin: viewMargin || '0px 0px 0px 0px',
          threshold: thresholdMargin || 0,
        },
      );
      inViewObserver.observe(elRef.current);
    } else {
      unSupportedCallback();
    }

    return () => {
      if (elRef && elRef.current && inViewObserver) {
        inViewObserver.unobserve(elRef.current);
      }
    };
  }, []);

  const idObj = {
    ...(id && { id }),
  };

  return (
    <div
      data-type="in_view"
      ref={elRef}
      className={`${className} ${
        wrapperClass ? s[wrapperClass] : ''
      } ${placeHolderClass}`}
      {...idObj}
    >
      {children}
    </div>
  );
};

InViewObserver.propTypes = {
  inViewCallback: PropTypes.func,
  className: PropTypes.string,
  ga: PropTypes.shape({
    category: PropTypes.string,
    action: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
  viewMargin: PropTypes.string,
  unSupportedCallback: PropTypes.func,
  doNotUnobserve: PropTypes.bool,
  wrapperClass: PropTypes.string,
  placeHolderClass: PropTypes.string,
  thresholdMargin: PropTypes.number,
  id: PropTypes.string,
};

InViewObserver.defaultProps = {
  inViewCallback: () => {},
  className: '',
  unSupportedCallback: () => {},
  ga: undefined,
  doNotUnobserve: false,
  viewMargin: '100px 0px 100px 0px',
  wrapperClass: undefined,
  placeHolderClass: '',
  thresholdMargin: 0,
  id: undefined,
};

export default InViewObserver;
